import React from "react";
import { useAppSelector } from "@src/store/hooks";
import { DateFormatsEnum, formatDate } from "../utils/dateUtils";
import { clx } from "../utils/stringUtils";
import styles from "./EventContextHeader.module.scss";

export type TContextListing = {
	section?: string | number | null;
	row?: string | number | null;
	seats?: string | number | null;
};

export type TContextEvent = {
	Event_Headliner?: string | null;
	Event_Name?: string | null;
	Event_Date?: string | null;
	Event_Time?: string | null;
	Venue_Name?: string | null;
	Venue_State?: string | null;
};

export const getListingEvents = (
	listings: ReadonlyArray<TContextEvent & { Event_ID?: number | null }>,
): Array<TContextEvent> | undefined => {
	const withEvent = listings.filter((x) => x.Event_Headliner || x.Event_Name);
	if (!withEvent.length) return undefined;
	const seen = new Set<string>();
	return withEvent.filter((x) => {
		const key = String(x.Event_ID ?? `${x.Event_Headliner ?? x.Event_Name}|${x.Event_Date ?? ""}`);
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
};

interface IEventContextHeaderProps {
	listings?: Array<TContextListing>;
	events?: Array<TContextEvent> | null;
	inline?: boolean;
	padding?: boolean;
}

const joinValues = (values: Array<string | number | null | undefined>): string =>
	values
		.map((v) => (v === null || v === undefined ? "" : String(v).trim()))
		.filter((v) => v.length > 0)
		.join(", ");

export const EventContextHeader: React.FC<IEventContextHeaderProps> = ({
	listings = [],
	events,
	inline = false,
	padding = false,
}) => {
	const storeEvent = useAppSelector((state) => state.pricing.selection.event);

	const eventList: Array<TContextEvent> = events ?? (storeEvent ? [storeEvent] : []);

	const listingParts: Array<string> = [];
	if (listings.length) {
		const plural = listings.length > 1;
		const sections = joinValues(listings.map((l) => l.section));
		const rows = joinValues(listings.map((l) => l.row));
		const seats = joinValues(listings.map((l) => l.seats));

		if (sections) listingParts.push(`${plural ? "Sections" : "Section"} ${sections}`);
		if (rows) listingParts.push(`${plural ? "Rows" : "Row"} ${rows}`);
		if (seats) listingParts.push(`${plural ? "Seats" : "Seat"} ${seats}`);
	}

	const renderEventParts = (event: TContextEvent | null): Array<string> => {
		const date = event?.Event_Date ? formatDate(event.Event_Date, DateFormatsEnum["MM/DD/YYYY"]) : "/";
		const time = event?.Event_Time || "/";
		const venue = event?.Venue_Name || "/";
		const venueLabel = event?.Venue_State ? `${venue}, ${event.Venue_State}` : venue;
		return [date, time, venueLabel];
	};

	const eventName = (event: TContextEvent | null): string => event?.Event_Headliner || event?.Event_Name || "/";

	const rootClass = clx(styles.root, inline ? styles.inline : "", padding ? styles.padded : "");

	if (eventList.length <= 1) {
		const event = eventList[0] ?? null;
		const rest = [...renderEventParts(event), ...listingParts];
		return (
			<div className={rootClass}>
				<span className={styles.primary}>{eventName(event)}</span>
				{rest.length ? `  ·  ${rest.join("  ·  ")}` : null}
			</div>
		);
	}

	return (
		<div className={rootClass}>
			{eventList.map((event, i) => {
				const rest = renderEventParts(event);
				return (
					<div key={i} className={styles.eventLine}>
						<span className={styles.primary}>{eventName(event)}</span>
						{`  ·  ${rest.join("  ·  ")}`}
					</div>
				);
			})}
			{listingParts.length ? <div className={styles.eventLine}>{listingParts.join("  ·  ")}</div> : null}
		</div>
	);
};

export default EventContextHeader;
